import _ from "lodash";
import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import ACLStore from "lib/aclStore";
import { getOrganizationDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getSettingValue } from "modules/PermissionModule";
import { parseJson, getYearDurationFormat } from "Utils";
import { store } from "store/createStore";
import ACLStorage from "lib/aclService";
import {
  getIsStaticWidgetisedHomepageEnabled,
  getIsCombineCurriculumHomePageEnabled,
} from "Platform/modules/PlatformUtils";
import { checkIsHomepageWidgetEnabled } from "HomePage/modules/HomePageUtils";
import { HOMEPAGE_WIDGETS_BE_KEYS } from "HomePage/modules/HomePageConstants";
import { getIsTimetablePlannerEnabled } from "Utils/ACLStoreUtils";

import { i18n } from "lib/i18n";
import moment from "moment";
import React from "react";
import {
  CAN_VIEW_TIMETABLE_MODULE_PERMISSION,
  CLASS_FEED_FILTERS_PREFERENCE,
  UPDATE_USER_SETTINGS,
  ENABLE_STUDENT_TIMETABLE_PERMISSION,
} from "Constants/permissionConstants";

export const EMPTY_ARRAY = [];
export const EMPTY_OBJECT = {};

export const getCalendarPerm = () => ACLStore.can("Common:PlatformCalendar");

export const getHomeScreenWidgetsPerm = ({ currentCurriculumProgramType }) => {
  switch (currentCurriculumProgramType) {
    case CURRICULUM_TYPE_DP:
      return ACLStore.can("FeatureFlag:DpShowHomeScreenWidgets");
    default:
      return ACLStore.can("FeatureFlag:ShowHomeScreenWidgets");
  }
};

export const getClassRoomTodoText = ({ userType, t }) => {
  if (userType !== "staff") {
    return t("common:assignments_todo");
  }

  return t("classRoom:assignments_manager");
};

export const isLtiServiceAvailable = (ltiDropDownOptions = [], serviceName) => {
  return _.some(ltiDropDownOptions, { serviceName });
};

export const mapleBearLikeConstants = {
  MAPLEBEAR: "MAPLEBEAR",
  COMMON_COURSE: "COMMONCOURSE",
};

export const findCountryUser = ({ userTags }) => {
  return _.find(
    userTags,
    tag => tag.key === "REGION" && tag.value !== "GLOBAL"
  );
};

export const mapleBearLikeCountryUser = ({ userTags }) => {
  return mapleBearLikeOrg() && findCountryUser({ userTags });
};

// country user but not global user
export const mapleBearLikeNonGlobalCountryUser = ({ userTags }) => {
  return Boolean(
    mapleBearLikeOrg() &&
      !isGlobalUser({ userTags }) &&
      findCountryUser({ userTags })
  );
};

export const mapleBearLikeOrg = () => {
  return ACLStore.can("FeatureFlag:IsMapleBearLikeOrg");
};

export const mapleBearLikeCentralOrg = ({ organizationTags = [] }) => {
  return Boolean(
    mapleBearLikeOrg() &&
      _.find(
        organizationTags,
        tag => tag.key === "MAPLEBEAR_ORG" && tag.value === "PARENT"
      )
  );
};

export const isMapleBearLikeEducator = ({ organizationTags = [] }) => {
  return Boolean(
    mapleBearLikeOrg() &&
      _.find(
        organizationTags,
        tag => tag.key === "MAPLEBEAR_ORG" && tag.value === "CHILD"
      )
  );
};

export const mapleBearLikeUser = ({ userTags }) => {
  return Boolean(
    mapleBearLikeOrg() && _.find(userTags, tag => tag.key === "REGION")
  );
};

export const mapleBearLikeCentralOrgGlobalUser = ({
  userTags = [],
  organizationTags = [],
}) => {
  return Boolean(
    mapleBearLikeCentralOrg({ organizationTags }) && isGlobalUser({ userTags })
  );
};

export const decideUserLevelType = ({ organizationId, userTags }) => {
  const isMapleBearOrg = mapleBearLikeOrg();

  if (isMapleBearOrg) {
    const organizationDetails = getOrganizationDetailsFromCache(organizationId);
    const organizationTags = _.get(organizationDetails, "tags", []);
    const isEducator = isMapleBearLikeEducator({ organizationTags });
    const globalUser = isGlobalUser({ userTags });

    return isEducator
      ? "educator"
      : globalUser
      ? "global_team"
      : "country_team";
  } else {
    return null;
  }
};

export const isGlobalUser = ({ userTags }) =>
  _.find(userTags, tag => tag.key === "REGION" && tag.value === "GLOBAL");

export const canUserAccessRegion = ({ value, userTags }) =>
  _.find(userTags, tag => tag.key === "REGION" && tag.value === value);

export const getUserRegions = ({ userTags }) =>
  _.filter(userTags, tag => tag.key === "REGION" && tag.value !== "GLOBAL");

export const shouldShowRegionTags = ({ organizationTags, userTags }) => {
  return (
    mapleBearLikeCentralOrg({ organizationTags }) &&
    (mapleBearLikeCentralOrgGlobalUser({ organizationTags, userTags }) ||
      _.get(getUserRegions({ userTags }), "length", 0) >= 1)
  );
};

// list of options in the dropdown menu
// Global user gets all region tags
// Country user gets only the user associated tags
export const getRegionOptions = ({ userTags, organizationTags, tags }) => {
  return mapleBearLikeCentralOrgGlobalUser({
    userTags,
    organizationTags,
  })
    ? tags
    : getUserRegions({ userTags });
};

export const doesTwoEntityBelongToSameRegion = (entityTags1, entityTags2) => {
  return Boolean(
    _.find(entityTags1, tag1 => {
      return (
        tag1.key === "REGION" &&
        _.find(entityTags2, tag2 => tag2.key === "REGION" && tag2.id == tag1.id)
      );
    })
  );
};

export const getUserCourseTagsFilters = ({ userTags }) => {
  //fetch tags for regions in which current user is.
  const regionTags = _.map(getUserRegions({ userTags }), region => region.id);
  const globalUser = isGlobalUser({ userTags });

  const filterTags = globalUser ? [] : regionTags;

  return _.size(filterTags) ? { tags: filterTags } : {};
};

export const isMapleBearLikeCommonCourse = ({ courseTags = [] }) => {
  return _.find(
    courseTags,
    tag =>
      tag.key === mapleBearLikeConstants.MAPLEBEAR &&
      tag.value === mapleBearLikeConstants.COMMON_COURSE
  );
};

export const disableEditForMapleBearChildOrg = ({
  shouldBypassDisable = false,
} = {}) => {
  return (
    mapleBearLikeOrg() &&
    ACLStore.can("FeatureFlag:MapleBearEditDisable") &&
    !shouldBypassDisable
  );
};

export const getEntityTagIds = ({ key, tags }) =>
  _.map(
    _.filter(tags, tag => tag.key === key),
    tag => tag.id
  );

export const getUnitPlanCentralRepoInputAndFilters = _.memoize(
  ({ courseId, academicYearId, organizationId }) => {
    const organizationDetails = getOrganizationDetailsFromCache(organizationId);
    const organizationTags = _.get(organizationDetails, "tags", []);
    const regionId = _.get(
      _.first(_.filter(organizationTags, tag => tag.key === "REGION")),
      "id",
      ""
    );
    const course = getCourseDetailsFromCache({
      id: courseId,
      courseGradeFilters: { academicYearId },
    });
    const courseTags = _.get(course, "tags", []);
    const courseRegionIds = _.map(
      _.filter(courseTags, { key: "REGION" }),
      "id"
    );
    const isEducator = isMapleBearLikeEducator({ organizationTags });
    const centralRepoCourseId = _.get(
      organizationDetails,
      "repoOrganization.commonCourseId",
      ""
    );
    const centralRepoFilters =
      isEducator && courseId === centralRepoCourseId
        ? { tags: [regionId] }
        : {};

    const isSchoolGroup = mapleBearLikeOrg();
    const centralRepoRegionInput = !_.isEmpty(courseRegionIds)
      ? courseRegionIds
      : regionId
      ? [regionId]
      : [];
    const centralRepoInput = {
      region: centralRepoRegionInput,
      isSchoolGroup,
    };
    return { centralRepoInput, centralRepoFilters };
  },
  JSON.stringify
);

export const getLastSelectedTab = ({ activeTab, userId, userEntityType }) => {
  const persistLastSelectedTab = ACLStore.can(
    "FeatureFlag:PersistLastSelectedMenu"
  );

  if (persistLastSelectedTab) {
    const userLastSelectedTab = getSettingValue({
      userId,
      name: `${userEntityType}_WEB:lastSelectedTab`,
      userEntityType,
      readFromUserAndOrgLevel: true,
    });

    return !_.isEmpty(userLastSelectedTab) ? userLastSelectedTab : activeTab;
  } else {
    return activeTab;
  }
};

export const rolesAndPermissionsShowMessagingCard = () => {
  return ACLStorage.getModuleSettingsValue("Module:FamilyMessaging");
};

export const getPaginationCourseFilters = _.memoize(
  ({
    curriculumProgramId,
    selectedArchivalState,
    classSearchText,
    academicYear = "",
    selectedGrades,
    selectedRegions,
    selectedSubjects,
    selectedCourseLevels,
    selectedSubjectGroups,
    mapleBearUserBasedFilters,
    options,
    currentCurriculumProgramType,
    isMapleBearLikeOrg,
    curriculumProgramIds,
    availableCurriculumTypes,
    isCombinedCurriculum = false,
    selectedClassView = "ALL_CLASSES",
  }) => {
    const courseFilters = {
      curriculumProgramIds: curriculumProgramIds
        ? curriculumProgramIds
        : [curriculumProgramId],
      archivalState: _.upperCase(selectedArchivalState),
      searchText: classSearchText,
      isOrderByStared: true,
      ...(!_.isEmpty(academicYear) && { academicYear }),
    };
    if (mapleBearUserBasedFilters?.tags?.length > 0) {
      courseFilters.tags = mapleBearUserBasedFilters?.tags;
    }

    if (_.difference(options?.grades, selectedGrades).length > 0) {
      if (_.size(selectedGrades) > 0) courseFilters.grades = selectedGrades;
    }

    if (_.difference(options?.regions, selectedRegions).length > 0) {
      // if mapleBearUserBasedFilters has returned some regions then append the tags with selectedRegions.
      const filterTags = _.get(courseFilters, "tags", []);
      _.forEach(selectedRegions, region => filterTags.push(region));
      courseFilters.tags = filterTags;
    }

    if (isCombinedCurriculum) {
      if (selectedCourseLevels.length > 0) {
        courseFilters.genericTags = selectedCourseLevels;
      }

      if (
        (_.includes(availableCurriculumTypes, CURRICULUM_TYPE_MYP) ||
          _.includes(availableCurriculumTypes, CURRICULUM_TYPE_DP)) &&
        selectedSubjectGroups.length > 0
      ) {
        courseFilters.subjectGroupIds = selectedSubjectGroups;
      }
    } else {
      if (
        _.difference(options?.courseLevels, selectedCourseLevels).length > 0
      ) {
        courseFilters.genericTags = selectedCourseLevels;
      }

      if (
        (currentCurriculumProgramType === CURRICULUM_TYPE_DP ||
          currentCurriculumProgramType === CURRICULUM_TYPE_MYP ||
          _.includes(availableCurriculumTypes, CURRICULUM_TYPE_MYP) ||
          _.includes(availableCurriculumTypes, CURRICULUM_TYPE_DP)) &&
        _.difference(options?.subjectGroups, selectedSubjectGroups).length > 0
      ) {
        courseFilters.subjectGroupIds = selectedSubjectGroups;
      }
    }

    if (
      currentCurriculumProgramType === CURRICULUM_TYPE_UBD &&
      _.difference(options?.subjects, selectedSubjects).length > 0
    ) {
      courseFilters.subjectIds = selectedSubjects;
    }

    if (selectedClassView == "MY_CLASSES") {
      courseFilters.fetchOnlyMappedCourses = true;
    } else {
      courseFilters.fetchOnlyMappedCourses = false;
    }

    return courseFilters;
  },
  JSON.stringify
);

const getTableColumnStyle = ({
  screenWidth,
  canViewWidgets,
  oldStyle,
  newStyle,
  _1080DeviceStyle,
  _1280DeviceStyle,
  widgetOffStyleFor1080,
  widgetOffStyleFor1280,
}) => {
  if (getIsStaticWidgetisedHomepageEnabled() || !canViewWidgets) {
    if (screenWidth <= 1080) {
      return widgetOffStyleFor1080;
    } else if (screenWidth <= 1280) {
      return widgetOffStyleFor1280;
    } else {
      return oldStyle;
    }
  }

  if (screenWidth <= 1080) return _1080DeviceStyle || oldStyle;
  if (screenWidth <= 1280) return _1280DeviceStyle || oldStyle;
  return newStyle;
};
export const getCoursesTableColumnsMemoized = _.memoize(
  ({
    t,
    isMapleBearLikeOrg,
    screenWidth,
    canViewWidgets,
    availableCurriculumTypes,
  }) => {
    const COLUMNS_LIST = [
      {
        label: t("common:class"),
        id: "class",
        style: getTableColumnStyle({
          screenWidth,
          canViewWidgets,
          oldStyle: { width: "448px" },
          newStyle: { width: "370px" },
          _1080DeviceStyle: { width: "200px" },
          _1280DeviceStyle: { width: "240px" },
          widgetOffStyleFor1080: { width: "240px" },
          widgetOffStyleFor1280: { width: "280px" },
        }),
        type: "CUSTOM",
      },
      {
        label: t("common:grade"),
        id: "gradeNameList",
        style: getTableColumnStyle({
          screenWidth,
          canViewWidgets,
          oldStyle: { width: "448px" },
          newStyle: { width: "370px" },
          _1080DeviceStyle: { width: "200px" },
          _1280DeviceStyle: { width: "240px" },
          widgetOffStyleFor1080: { width: "240px" },
          widgetOffStyleFor1280: { width: "280px" },
        }),
        type: "CUSTOM",
      },
      {
        id: "bookmarkCourse",
        style: { minWidth: "16px" },
        type: "CUSTOM",
      },
    ];

    // for maple bear add count column at second last position
    if (isMapleBearLikeOrg) {
      //adjust width of class & grade column
      _.forEach(COLUMNS_LIST, column => {
        if (column.id == "class")
          column.style = getTableColumnStyle({
            screenWidth,
            canViewWidgets,
            oldStyle: { width: "300px" },
            newStyle: { width: "200px" },
            _1080DeviceStyle: { width: "100px" },
            _1280DeviceStyle: { width: "140px" },
            widgetOffStyleFor1080: { width: "140px" },
            widgetOffStyleFor1280: { width: "180px" },
          });
        if (column.id === "gradeNameList")
          column.style = getTableColumnStyle({
            screenWidth,
            canViewWidgets,
            oldStyle: { width: "140px" },
            newStyle: { width: "100px" },
            _1080DeviceStyle: { width: "50px" },
            _1280DeviceStyle: { width: "70px" },
            widgetOffStyleFor1080: { width: "70px" },
            widgetOffStyleFor1280: { width: "50px" },
          });
      });
      COLUMNS_LIST.splice(
        1,
        0,
        {
          label: t("common:SUBJECT"),
          id: "subjectNameList",
          type: "CUSTOM",
          style: getTableColumnStyle({
            screenWidth,
            canViewWidgets,
            oldStyle: { width: "280px", whiteSpace: "unset" },
            newStyle: { width: "180px", whiteSpace: "unset" },
            _1080DeviceStyle: { width: "90px", whiteSpace: "unset" },
            _1280DeviceStyle: { width: "100px", whiteSpace: "unset" },
            widgetOffStyleFor1080: { width: "130px", whiteSpace: "unset" },
            widgetOffStyleFor1280: { width: "140px", whiteSpace: "unset" },
          }),
        },
        {
          label: t("common:region_plural"),
          id: "regions",
          type: "CUSTOM",
          style: { width: "78px" },
        }
      );
    }

    // for MYP curriculum add subjects column at 3rd position
    if (
      (_.includes(availableCurriculumTypes, CURRICULUM_TYPE_MYP) ||
        _.includes(availableCurriculumTypes, CURRICULUM_TYPE_UBD)) &&
      !_.includes(availableCurriculumTypes, CURRICULUM_TYPE_DP)
    ) {
      //adjust width of class & grade column
      _.forEach(COLUMNS_LIST, column => {
        if (column.id === "class") {
          column.style = getTableColumnStyle({
            screenWidth,
            canViewWidgets,
            oldStyle: { width: "448px" },
            newStyle: { width: "330px" },
            _1080DeviceStyle: { width: "200px" },
            _1280DeviceStyle: { width: "240px" },
            widgetOffStyleFor1080: { width: "240px" },
            widgetOffStyleFor1280: { width: "280px" },
          });
        }
        if (column.id === "gradeNameList")
          column.style = getTableColumnStyle({
            screenWidth,
            canViewWidgets,
            oldStyle: {
              width: "180px",
            },
            newStyle: {
              width: "160px",
            },
            _1080DeviceStyle: {
              width: "70px",
            },
            _1280DeviceStyle: {
              width: "90px",
            },
            widgetOffStyleFor1080: {
              width: "110px",
            },
            widgetOffStyleFor1280: {
              width: "130px",
            },
          });
      });
      if (!isMapleBearLikeOrg) {
        COLUMNS_LIST.splice(2, 0, {
          label: t("common:SUBJECT"),
          id: "subjectNameList",
          type: "CUSTOM",
          style: getTableColumnStyle({
            screenWidth,
            canViewWidgets,
            oldStyle: { width: "280px", whiteSpace: "unset" },
            newStyle: { width: "230px", whiteSpace: "unset" },
            _1080DeviceStyle: { width: "120px", whiteSpace: "unset" },
            _1280DeviceStyle: { width: "140px", whiteSpace: "unset" },
            widgetOffStyleFor1080: { width: "160px", whiteSpace: "unset" },
            widgetOffStyleFor1280: { width: "180px", whiteSpace: "unset" },
          }),
        });
      }
    }

    // for DP add subject-levels and subjects column at 3rd & 4th position respectively
    if (_.includes(availableCurriculumTypes, CURRICULUM_TYPE_DP)) {
      //adjust width of class & grade column
      _.forEach(COLUMNS_LIST, column => {
        if (column.id == "class")
          column.style = getTableColumnStyle({
            screenWidth,
            canViewWidgets,
            oldStyle: {
              width: "380px",
            },
            newStyle: {
              width: "280px",
            },
            _1080DeviceStyle: {
              width: "140px",
            },
            _1280DeviceStyle: {
              width: "180px",
            },
            widgetOffStyleFor1080: {
              width: "180px",
            },
            widgetOffStyleFor1280: {
              width: "220px",
            },
          });
        if (column.id === "gradeNameList")
          column.style = getTableColumnStyle({
            screenWidth,
            canViewWidgets,
            oldStyle: {
              width: "120px",
            },
            newStyle: {
              width: "120px",
            },
            _1080DeviceStyle: {
              width: "80px",
            },
            _1280DeviceStyle: {
              width: "100px",
            },
            widgetOffStyleFor1080: {
              width: "120px",
            },
            widgetOffStyleFor1280: {
              width: "140px",
            },
          });
      });
      COLUMNS_LIST.splice(2, 0, {
        label: t("common:level"),
        id: "courseLevel",
        type: "CUSTOM",
        style: getTableColumnStyle({
          screenWidth,
          canViewWidgets,
          oldStyle: { width: "120px" },
          newStyle: { width: "80px" },
          _1080DeviceStyle: { width: "80px" },
          _1280DeviceStyle: { width: "80px" },
          widgetOffStyleFor1080: { width: "120px" },
          widgetOffStyleFor1280: { width: "120px" },
        }),
      });
      if (!isMapleBearLikeOrg) {
        COLUMNS_LIST.splice(3, 0, {
          label: t("common:SUBJECT"),
          id: "subjectNameList",
          type: "CUSTOM",
          style: getTableColumnStyle({
            screenWidth,
            canViewWidgets,
            oldStyle: { width: "260px" },
            newStyle: { width: "200px" },
            _1080DeviceStyle: { width: "100px" },
            _1280DeviceStyle: { width: "100px" },
            widgetOffStyleFor1080: { width: "200px" },
            widgetOffStyleFor1280: { width: "200px" },
          }),
        });
      }
    }

    return COLUMNS_LIST;
  },
  params => JSON.stringify([params, i18n.language])
);

export const isActiveTabUpcomingAndTodosEnabled = activeTab => {
  return _.isEqual("UPCOMING", activeTab);
};

export const getStartDateAndEndDateOfDeadline = (day = 30) => {
  const startDate = moment().startOf("day").utc().format();
  const endDate = moment(startDate)
    .add(day, "days")
    .utc()
    .endOf("day")
    .format();
  return { startDate, endDate };
};

export const getDeadLineDate = () => {
  return getStartDateAndEndDateOfDeadline();
};

export const isDemoDayBannerVisiblityEnabled = () => {
  return ACLStore.can("FeatureFlag:ShowDemoDayBanner");
};

export const isShowProductUpdatePopUpEnabled = () => {
  return ACLStore.can("Common:ShowProductUpdatePopUp");
};

export const isProductUpdateV2Enabled = () => {
  return ACLStore.can("FeatureFlag:EnableProductUpdateV2");
};

export const isTimetableWidgetEnabled = () => {
  const { timetableWidget } = HOMEPAGE_WIDGETS_BE_KEYS;

  if (getIsCombineCurriculumHomePageEnabled()) {
    return checkIsHomepageWidgetEnabled(timetableWidget);
  }

  return (
    isTimetableModuleEnabled() &&
    ACLStore.can("FeatureFlag:ShowTimetableWidget")
  );
};

export const isTimetableModuleEnabled = ({
  curriculumProgramId,
  isUserTeacher = true,
} = {}) => {
  if (!isUserTeacher) {
    return ACLStore.can(ENABLE_STUDENT_TIMETABLE_PERMISSION);
  }

  if (_.isNil(curriculumProgramId)) {
    return ACLStorage.getModuleSettingsValue(
      CAN_VIEW_TIMETABLE_MODULE_PERMISSION
    );
  }

  const context = [
    {
      entityType: "CURRICULUM_PROGRAM",
      entityId: curriculumProgramId,
    },
  ];
  return ACLStorage.getModuleSettingsValueByContext(
    CAN_VIEW_TIMETABLE_MODULE_PERMISSION,
    context
  );
};

export const getIsTimetablePlannerEnabledOnHomepage = () => {
  const { timetablePlannerWidget } = HOMEPAGE_WIDGETS_BE_KEYS;

  if (getIsCombineCurriculumHomePageEnabled()) {
    return checkIsHomepageWidgetEnabled(timetablePlannerWidget);
  }

  return isTimetableModuleEnabled() && getIsTimetablePlannerEnabled();
};

export const getAcademicTermIds = ({
  gradingPeriods,
  selectedGradingPeriodIds = [],
}) => {
  const selectedIds = selectedGradingPeriodIds;

  if (_.isEmpty(selectedIds)) {
    return _.map(gradingPeriods, "id");
  }

  return selectedIds;
};

export const getFilteredCoursesByGradingPeriods = ({
  courses,
  gradingPeriods,
  selectedGradingPeriodIds,
}) => {
  const userSelectedGradingPeriodIds = getAcademicTermIds({
    gradingPeriods,
    selectedGradingPeriodIds,
  });

  return _.filter(courses, course => {
    return _.some(_.get(course, "learningCourse", []), learningCourse => {
      const gradingPeriodIds = _.map(
        _.get(learningCourse, "gradingPeriods.edges", []),
        "node.id"
      );

      return _.some(gradingPeriodIds, id =>
        _.includes(userSelectedGradingPeriodIds, id)
      );
    });
  });
};

export const getGradingPeriodFilterMenuEntity = ({ gradingPeriods, t }) => [
  {
    key: "gradingPeriods",
    label: t("common:grading_period_plural"),
    allOptionsLabel: t("common:all_label", {
      label: t("common:grading_period_plural"),
    }),
    options: _.map(gradingPeriods, term => {
      const id = _.get(term, "id");
      const label = _.get(term, "label", "");
      const startDate = _.get(term, "startDate");
      const endDate = _.get(term, "endDate");

      return {
        key: id,
        value: id,
        label: `${label} (${getYearDurationFormat({ startDate, endDate })})`,
        parent: "gradingPeriods",
      };
    }),
  },
];

export const GRADING_PERIOD_IDS_KEY = "gradingPeriodIds";

export const getSavedClassFeedFilterPreference = () =>
  parseJson(
    getSettingValue({
      name: CLASS_FEED_FILTERS_PREFERENCE,
      readFromUserAndOrgLevel: true,
    })
  ) || {};

export const getShowGradingPeriodFilter = ({
  isLearningCoursesEnabled,
  gradingPeriods,
}) => {
  const state = store?.getState();
  const userType = _.get(state, "login.userInfo.user_type");

  return (
    userType !== "staff" &&
    isLearningCoursesEnabled &&
    !_.isEmpty(gradingPeriods)
  );
};

export const updateGradingPeriodFilter = ({
  selectedKeys,
  gradingPeriodPath,
  coursesFiltersFromRedux,
  updateCoursesFiltersInRedux,
  updateSettings,
  currentGradingPeriodIds = [],
}) => {
  const gradingPeriodIds = _.flatMap(_.values(selectedKeys));

  if (
    _.isEqual(_.sortBy(gradingPeriodIds), _.sortBy(currentGradingPeriodIds))
  ) {
    return;
  }

  const updatedGradingPeriods = _.setWith(
    _.cloneDeep(_.get(coursesFiltersFromRedux, "gradingPeriods", {})),
    gradingPeriodPath,
    gradingPeriodIds,
    Object
  );

  updateCoursesFiltersInRedux({
    ...coursesFiltersFromRedux,
    gradingPeriods: updatedGradingPeriods,
  });

  const existingPref = getSavedClassFeedFilterPreference();

  const updatedPref = _.setWith(
    _.cloneDeep(existingPref),
    gradingPeriodPath,
    gradingPeriodIds,
    Object
  );

  updateSettings({
    settings: [
      {
        name: CLASS_FEED_FILTERS_PREFERENCE,
        value: JSON.stringify(updatedPref),
      },
    ],
    showToastMsg: false,
    mutationVariablesSrc: UPDATE_USER_SETTINGS,
    refetchConfiguration: false,
  });
};
